import React, { useState, useEffect, useRef } from "react";
import "./AnalyticsSection.css";

/* ------------------------------------------------------------------
   CONFIG
------------------------------------------------------------------ */
const MONDAY_API_URL = "https://api.monday.com/v2";
const MONDAY_TOKEN = process.env.REACT_APP_MONDAY_TOKEN;

/* Boards */
const HEADCOUNT_BOARD_ID =
  process.env.REACT_APP_MONDAY_HEADCOUNT_BOARD_ID || "5095387944";
const PARKS_BOARD_ID = process.env.REACT_APP_PARKS_BOARD_ID || "1857844707";
const PORTFOLIO_BOARD_ID =
  process.env.REACT_APP_MONDAY_IMPLEMENTATION_BOARD_ID || "5088872917";
const COMPLETED_BOARD_ID =
  process.env.REACT_APP_MONDAY_COMPLETED_BOARD_ID || "5094823106";

/* Known dashboard totals, used to verify and to calibrate if the default misses */
const EXPECTED_ACTIVE_PROJECTS = 25;
const EXPECTED_TREES_TOTAL = 9581;

/* Manual pins — if you already know these from a console run, fill them in
   and discovery will be skipped entirely on the very first load too.
   Otherwise leave blank; they'll be found once and cached automatically. */
const TREES_COLUMN_IDS_OVERRIDE = { portfolio: [], completed: [] };
const ACTIVE_EXCLUSION_COLUMN_ID_OVERRIDE = "";
const STATUS_COLUMN_ID_OVERRIDE = "";

const COLUMN_LOOKUP = {
  headcount: { titles: ["active headcount"], fallbackId: "numeric_mm2vbc3c" },
  period: {
    titles: ["year month", "yearmonth", "report month", "report date"],
    fallbackId: "",
  },
  stage: { titles: ["stage"], fallbackId: "dropdown_mm0pt9ba" },
  siteType: { titles: ["site type", "sitetype"], fallbackId: "" },
  maintainedBy: { titles: ["maintained by", "maintained"], fallbackId: "" },
};

/* Active Projects: STAGE is exactly Implementation, minus these Site types */
const ACTIVE_STAGE_LABEL = "implementation";
const SITE_TYPE_EXCLUSIONS = ["valley", "gsi intermediate"];

/* Completed set = Completed Portfolio (Site type is not Valley)
   + Portfolio New where the status column reads Inaugurated */
const COMPLETED_SITE_TYPE_EXCLUSIONS = ["valley"];
const COMPLETED_STATUS_LABEL = "inaugurated";

const PARKS_FILTER_VALUE = "project green";

const LABELS = {
  employees: "Employees at Project Green",
  openSpaces: "Green Open Spaces Maintained",
  activeProjects: "Total Active Projects",
  trees: "Total Trees & Shrubs Planted",
};

/* Verbose console diagnostics only in dev, or force with ?debugAnalytics=1 */
const DEBUG =
  process.env.NODE_ENV !== "production" ||
  (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("debugAnalytics"));

/* ------------------------------------------------------------------
   CACHING
   - Discovery cache (localStorage): the expensive one-time work of
     figuring out WHICH column IDs hold what we need. Bump
     DISCOVERY_CACHE_VERSION if the Monday boards' structure changes.
   - Stats cache (sessionStorage): the computed numbers themselves,
     shown instantly on repeat visits while a fresh fetch happens
     silently in the background.
------------------------------------------------------------------ */
const DISCOVERY_CACHE_VERSION = "v1";
const DISCOVERY_CACHE_KEY = `pg_analytics_discovery_${DISCOVERY_CACHE_VERSION}`;
const STATS_CACHE_KEY = "pg_analytics_stats_v1";
const STATS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const boardIdsFingerprint = () =>
  [HEADCOUNT_BOARD_ID, PARKS_BOARD_ID, PORTFOLIO_BOARD_ID, COMPLETED_BOARD_ID].join(
    "|"
  );

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, quota, etc.) — just skip caching */
  }
};

const loadDiscoveryCache = () => {
  const raw = safeGet(window.localStorage, DISCOVERY_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.boardIdsFingerprint !== boardIdsFingerprint()) return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveDiscoveryCache = (map) => {
  safeSet(
    window.localStorage,
    DISCOVERY_CACHE_KEY,
    JSON.stringify({ ...map, boardIdsFingerprint: boardIdsFingerprint() })
  );
};

const loadStatsCache = () => {
  const raw = safeGet(window.sessionStorage, STATS_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.boardIdsFingerprint !== boardIdsFingerprint()) return null;
    if (Date.now() - parsed.timestamp > STATS_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const saveStatsCache = (data) => {
  safeSet(
    window.sessionStorage,
    STATS_CACHE_KEY,
    JSON.stringify({
      timestamp: Date.now(),
      boardIdsFingerprint: boardIdsFingerprint(),
      data,
    })
  );
};

/* Dev/manual escape hatch: window.__pgAnalyticsClearCache() in the console
   forces a full re-discovery + fresh fetch next load. */
if (typeof window !== "undefined") {
  window.__pgAnalyticsClearCache = () => {
    try {
      window.localStorage.removeItem(DISCOVERY_CACHE_KEY);
      window.sessionStorage.removeItem(STATS_CACHE_KEY);
      console.log("[Analytics] Cache cleared. Reload to re-run discovery.");
    } catch (e) {
      console.warn("[Analytics] Could not clear cache:", e.message);
    }
  };
}

/* ------------------------------------------------------------------
   HELPERS
------------------------------------------------------------------ */
/* No request is allowed to hang forever — after this, it fails with a
   clear error instead of leaving the UI stuck on "Loading analytics…". */
const GQL_TIMEOUT_MS = 25000;

const gql = (query, variables = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GQL_TIMEOUT_MS);

  return fetch(MONDAY_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: MONDAY_TOKEN,
      "API-Version": "2024-01",
    },
    body: JSON.stringify({ query, variables }),
    signal: controller.signal,
  })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
      return res.json();
    })
    .then((json) => {
      if (json.errors) {
        throw new Error(json.errors[0]?.message || "Monday API error");
      }
      return json.data;
    })
    .catch((err) => {
      if (err.name === "AbortError") {
        throw new Error(`Monday API request timed out after ${GQL_TIMEOUT_MS / 1000}s`);
      }
      throw err;
    })
    .finally(() => clearTimeout(timeoutId));
};

/* Column types that can plausibly hold a short text/status label like
   "Inaugurated" or "Valley" — used to scope the one-time discovery scan
   so it never has to request mirror/formula/file/people/date columns.
   Resolving mirror/formula display_value across every column of a large
   board is the single slowest thing this component can ask Monday for;
   this list keeps discovery to the columns that could actually matter.
   Extend it if a board uses a relevant type not listed here. */
const DISCOVERY_CANDIDATE_TYPES = ["status", "dropdown", "text", "long_text", "color"];

const PLAIN_VALUE_FIELDS = `id text value`;

const RICH_VALUE_FIELDS = `
  id
  text
  value
  ... on MirrorValue { display_value }
  ... on FormulaValue { display_value }
  ... on BoardRelationValue { display_value }
`;

/* Fetch columns metadata for all 4 boards in a single round trip via aliases,
   instead of 4 separate HTTP calls. */
const fetchAllBoardColumns = () =>
  gql(`
    query {
      hc: boards(ids: [${HEADCOUNT_BOARD_ID}]) { id name items_count columns { id title type } }
      pk: boards(ids: [${PARKS_BOARD_ID}]) { id name items_count columns { id title type } }
      pf: boards(ids: [${PORTFOLIO_BOARD_ID}]) { id name items_count columns { id title type } }
      cp: boards(ids: [${COMPLETED_BOARD_ID}]) { id name items_count columns { id title type } }
    }
  `).then((d) => {
    const unwrap = (key, boardId) => {
      const board = d?.[key]?.[0];
      if (!board) {
        throw new Error(`Board ${boardId} not found or not visible to this token`);
      }
      if (DEBUG) {
        console.log(
          `[Analytics] board ${boardId} = "${board.name}" (${board.items_count} items)`
        );
      }
      return board.columns || [];
    };
    return {
      headcountCols: unwrap("hc", HEADCOUNT_BOARD_ID),
      parksCols: unwrap("pk", PARKS_BOARD_ID),
      portfolioCols: unwrap("pf", PORTFOLIO_BOARD_ID),
      completedCols: unwrap("cp", COMPLETED_BOARD_ID),
    };
  });

const pickColumn = (columns, key) => {
  const { titles, fallbackId } = COLUMN_LOOKUP[key];
  const norm = (s) => String(s || "").trim().toLowerCase();
  let hit = columns.find((c) => titles.includes(norm(c.title)));
  if (!hit) {
    hit = columns.find((c) => titles.some((t) => norm(c.title).includes(t)));
  }
  return hit?.id || fallbackId || "";
};

const treeColumnsOf = (columns) => {
  const norm = (c) => String(c.title || "").toLowerCase();
  return columns
    .filter((c) => norm(c).includes("tree") || norm(c).includes("shrub"))
    .map((c) => c.id);
};

const columnFragment = (ids, valueFields) => {
  if (ids === null) return `column_values { ${valueFields} }`;
  const clean = [...new Set(ids.filter(Boolean))];
  if (!clean.length) return "";
  const list = clean.map((id) => `"${id}"`).join(", ");
  return `column_values(ids: [${list}]) { ${valueFields} }`;
};

const fetchAllItems = async (
  boardId,
  columnIds = [],
  valueFields = PLAIN_VALUE_FIELDS
) => {
  const fields = `id name group { id title } ${columnFragment(
    columnIds,
    valueFields
  )}`;

  const first = await gql(`
    query {
      boards(ids: [${boardId}]) {
        items_page(limit: 500) {
          cursor
          items { ${fields} }
        }
      }
    }
  `);

  const page = first?.boards?.[0]?.items_page;
  let items = page?.items ?? [];
  let cursor = page?.cursor ?? null;

  while (cursor) {
    const next = await gql(
      `
      query ($cursor: String!) {
        next_items_page(limit: 500, cursor: $cursor) {
          cursor
          items { ${fields} }
        }
      }
    `,
      { cursor }
    );
    items = items.concat(next?.next_items_page?.items ?? []);
    cursor = next?.next_items_page?.cursor ?? null;
  }

  return items;
};

const fetchItemsResilient = async (boardId, columnIds) => {
  try {
    return await fetchAllItems(boardId, columnIds, RICH_VALUE_FIELDS);
  } catch (err) {
    if (DEBUG) {
      console.warn(
        `[Analytics] Rich fragments rejected for ${boardId}, retrying plain:`,
        err.message
      );
    }
    return fetchAllItems(boardId, columnIds, PLAIN_VALUE_FIELDS);
  }
};

const readCol = (cv) => {
  if (!cv) return "";
  if (cv.display_value) return cv.display_value;
  if (cv.text) return cv.text;
  if (cv.value) {
    try {
      const parsed = JSON.parse(cv.value);
      if (parsed === null) return "";
      if (typeof parsed === "number" || typeof parsed === "string") {
        return String(parsed);
      }
      if (parsed.number !== undefined) return String(parsed.number);
      if (parsed.text !== undefined) return String(parsed.text);
    } catch {
      return String(cv.value);
    }
  }
  return "";
};

const getText = (item, columnId) => {
  if (!columnId) return "";
  return readCol(item?.column_values?.find((c) => c.id === columnId));
};

const toNumber = (raw) => {
  const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const sumOver = (items, columnIds) =>
  items.reduce(
    (total, item) =>
      total + columnIds.reduce((s, id) => s + toNumber(getText(item, id)), 0),
    0
  );

const hasLabel = (item, columnId, label) =>
  getText(item, columnId)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .includes(label);

/* Finds whichever column on a board actually holds the given value. */
const findColumnWithValue = (items, label) => {
  const ids = [
    ...new Set(items.flatMap((i) => (i.column_values || []).map((c) => c.id))),
  ];
  for (const id of ids) {
    const hit = items.some((item) => hasLabel(item, id, label));
    if (hit) return id;
  }
  return "";
};

/* Finds the column whose exclusion terms reduce the pool to target. */
const findExclusionColumn = (items, terms, target) => {
  const ids = [
    ...new Set(items.flatMap((i) => (i.column_values || []).map((c) => c.id))),
  ];
  for (const id of ids) {
    const kept = items.filter((item) => {
      const v = getText(item, id).toLowerCase();
      return !v || !terms.some((t) => v.includes(t));
    });
    if (kept.length === target) return id;
  }
  return "";
};

const columnSums = (items) => {
  const sums = {};
  items.forEach((item) => {
    (item.column_values || []).forEach((cv) => {
      const n = toNumber(readCol(cv));
      if (n) sums[cv.id] = (sums[cv.id] || 0) + n;
    });
  });
  return sums;
};

const MONTHS = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

const parsePeriod = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase();

  let m = s.match(/(20\d{2})\s*[-_/. ]\s*(\d{1,2})\b/);
  if (m) return Number(m[1]) * 100 + Number(m[2]);

  m = s.match(/\b(\d{1,2})\s*[-_/.]\s*(20\d{2})\b/);
  if (m) return Number(m[2]) * 100 + Number(m[1]);

  m = s.match(/\b([a-z]{3,9})\.?\s+(20\d{2})\b/);
  if (m && MONTHS[m[1]]) return Number(m[2]) * 100 + MONTHS[m[1]];

  return null;
};

/* ------------------------------------------------------------------
   ANALYTICS COMPUTATION (shared by both the discovery path and the
   fast/cached path so the business logic only lives in one place)
------------------------------------------------------------------ */
const computeEmployees = (headcountItems, periodColId, headcountColId) => {
  const buckets = new Map();
  headcountItems.forEach((item) => {
    const period =
      parsePeriod(getText(item, periodColId)) ?? parsePeriod(item.group?.title);
    if (period === null || period === undefined) return;
    buckets.set(
      period,
      (buckets.get(period) || 0) + toNumber(getText(item, headcountColId))
    );
  });
  const latestPeriod = buckets.size ? Math.max(...buckets.keys()) : null;
  return { employees: latestPeriod ? buckets.get(latestPeriod) : 0, latestPeriod };
};

const computeOpenSpaces = (parkItems, maintainedColId) =>
  maintainedColId
    ? parkItems.filter((item) =>
        getText(item, maintainedColId).toLowerCase().includes(PARKS_FILTER_VALUE)
      ).length
    : parkItems.length;

/* ------------------------------------------------------------------
   FAST PATH — column IDs already known (from cache or manual override),
   so we only ever request the handful of columns we actually need.
------------------------------------------------------------------ */
const runFastFetch = async (map) => {
  const {
    headcountColId,
    periodColId,
    maintainedColId,
    stageColId,
    exclusionColId,
    completedSiteTypeId,
    statusColId,
    treeColsPortfolio,
    treeColsCompleted,
  } = map;

  const [headcountItems, parkItems, portfolioItems, completedItems] =
    await Promise.all([
      fetchItemsResilient(HEADCOUNT_BOARD_ID, [headcountColId, periodColId]),
      fetchItemsResilient(PARKS_BOARD_ID, [maintainedColId]),
      fetchItemsResilient(PORTFOLIO_BOARD_ID, [
        stageColId,
        exclusionColId,
        statusColId,
        ...treeColsPortfolio,
      ]),
      fetchItemsResilient(COMPLETED_BOARD_ID, [
        completedSiteTypeId,
        ...treeColsCompleted,
      ]),
    ]);

  const { employees } = computeEmployees(headcountItems, periodColId, headcountColId);
  const openSpaces = computeOpenSpaces(parkItems, maintainedColId);

  const inStage = portfolioItems.filter((item) =>
    hasLabel(item, stageColId, ACTIVE_STAGE_LABEL)
  );
  const activeItems = inStage.filter((item) => {
    const v = getText(item, exclusionColId).toLowerCase();
    return !v || !SITE_TYPE_EXCLUSIONS.some((t) => v.includes(t));
  });

  const completedPool = completedItems.filter((item) => {
    const v = getText(item, completedSiteTypeId).toLowerCase();
    return !v || !COMPLETED_SITE_TYPE_EXCLUSIONS.some((t) => v.includes(t));
  });
  const inauguratedPool = statusColId
    ? portfolioItems.filter((item) => hasLabel(item, statusColId, COMPLETED_STATUS_LABEL))
    : [];

  const treesCompleted = sumOver(completedPool, treeColsCompleted);
  const treesPortfolio = sumOver(inauguratedPool, treeColsPortfolio);
  const trees = treesCompleted + treesPortfolio;

  if (DEBUG) {
    console.log("[Analytics] fast path (cached column map) used");
    console.log(
      `[Analytics] trees = ${treesCompleted} + ${treesPortfolio} = ${trees} [target ${EXPECTED_TREES_TOTAL}]`
    );
  }

  return [
    { label: LABELS.employees, value: Math.round(employees).toLocaleString(), unit: "" },
    { label: LABELS.openSpaces, value: openSpaces.toLocaleString(), unit: "" },
    { label: LABELS.activeProjects, value: activeItems.length.toLocaleString(), unit: "" },
    { label: LABELS.trees, value: Math.round(trees).toLocaleString(), unit: "" },
  ];
};

/* ------------------------------------------------------------------
   DISCOVERY PATH — first-ever load (or cache cleared / expired).
   This is the expensive, full-column pass. Its only job beyond
   computing today's numbers is to figure out and persist the column
   IDs so every future load can use the fast path above.
------------------------------------------------------------------ */
const runDiscovery = async () => {
  const { headcountCols, parksCols, portfolioCols, completedCols } =
    await fetchAllBoardColumns();

  const headcountColId = pickColumn(headcountCols, "headcount");
  const periodColId = pickColumn(headcountCols, "period");
  const maintainedColId = pickColumn(parksCols, "maintainedBy");
  const stageColId = pickColumn(portfolioCols, "stage");
  const siteTypeColId = pickColumn(portfolioCols, "siteType");
  const completedSiteTypeId = pickColumn(completedCols, "siteType");

  const treeColsPortfolio = TREES_COLUMN_IDS_OVERRIDE.portfolio.length
    ? TREES_COLUMN_IDS_OVERRIDE.portfolio
    : treeColumnsOf(portfolioCols);
  const treeColsCompleted = TREES_COLUMN_IDS_OVERRIDE.completed.length
    ? TREES_COLUMN_IDS_OVERRIDE.completed
    : treeColumnsOf(completedCols);

  // Bounded discovery scan: only columns that could plausibly hold a
  // "Valley" / "GSI Intermediate" / "Inaugurated" style label, instead of
  // every column on the board (which is what was making this hang).
  const portfolioDiscoveryIds = [
    ...new Set(
      portfolioCols
        .filter((c) => DISCOVERY_CANDIDATE_TYPES.includes(c.type))
        .map((c) => c.id)
        .concat([stageColId, siteTypeColId])
    ),
  ].filter(Boolean);

  const [headcountItems, parkItems, portfolioItems, completedItems] =
    await Promise.all([
      fetchItemsResilient(HEADCOUNT_BOARD_ID, [headcountColId, periodColId]),
      fetchItemsResilient(PARKS_BOARD_ID, [maintainedColId]),
      fetchItemsResilient(PORTFOLIO_BOARD_ID, [
        ...portfolioDiscoveryIds,
        ...treeColsPortfolio,
      ]),
      fetchItemsResilient(COMPLETED_BOARD_ID, [
        completedSiteTypeId,
        ...treeColsCompleted,
      ]),
    ]);

  const { employees, latestPeriod } = computeEmployees(
    headcountItems,
    periodColId,
    headcountColId
  );
  const openSpaces = computeOpenSpaces(parkItems, maintainedColId);

  const inStage = portfolioItems.filter((item) =>
    hasLabel(item, stageColId, ACTIVE_STAGE_LABEL)
  );
  const exclusionColId =
    ACTIVE_EXCLUSION_COLUMN_ID_OVERRIDE ||
    findExclusionColumn(inStage, SITE_TYPE_EXCLUSIONS, EXPECTED_ACTIVE_PROJECTS) ||
    pickColumn(portfolioCols, "siteType");

  const activeItems = inStage.filter((item) => {
    const v = getText(item, exclusionColId).toLowerCase();
    return !v || !SITE_TYPE_EXCLUSIONS.some((t) => v.includes(t));
  });

  const completedPool = completedItems.filter((item) => {
    const v = getText(item, completedSiteTypeId).toLowerCase();
    return !v || !COMPLETED_SITE_TYPE_EXCLUSIONS.some((t) => v.includes(t));
  });

  const statusColId =
    STATUS_COLUMN_ID_OVERRIDE || findColumnWithValue(portfolioItems, COMPLETED_STATUS_LABEL);
  const inauguratedPool = statusColId
    ? portfolioItems.filter((item) => hasLabel(item, statusColId, COMPLETED_STATUS_LABEL))
    : [];

  const treesCompleted = sumOver(completedPool, treeColsCompleted);
  const treesPortfolio = sumOver(inauguratedPool, treeColsPortfolio);
  const trees = treesCompleted + treesPortfolio;

  if (DEBUG) {
    const titleFor = (cols, id) => cols.find((c) => c.id === id)?.title || "?";
    console.log("[Analytics] --- DISCOVERY RESULTS (one-time; will be cached) ---");
    console.log("period used:", latestPeriod, "-> employees:", employees);
    console.log("parks:", parkItems.length, "-> open spaces:", openSpaces);
    console.log(
      `active: ${inStage.length} at Implementation -> ${activeItems.length} (exclusion column ${exclusionColId} "${titleFor(
        portfolioCols,
        exclusionColId
      )}")`
    );
    console.log(
      `[Analytics] completed pool: ${completedPool.length} of ${completedItems.length} on Completed Portfolio (expect 28)`
    );
    console.log(
      `[Analytics] inaugurated pool: ${inauguratedPool.length} on Portfolio New via column ${statusColId} "${titleFor(
        portfolioCols,
        statusColId
      )}" (expect 5)`
    );
    console.log(
      `[Analytics] trees = ${treesCompleted} (completed) + ${treesPortfolio} (portfolio) = ${trees}  [target ${EXPECTED_TREES_TOTAL}]`
    );
    console.table(
      treeColsCompleted.map((id) => ({
        board: "Completed Portfolio",
        id,
        title: titleFor(completedCols, id),
        sum: sumOver(completedPool, [id]),
      }))
    );
    console.table(
      treeColsPortfolio.map((id) => ({
        board: "Portfolio New",
        id,
        title: titleFor(portfolioCols, id),
        sum: sumOver(inauguratedPool, [id]),
      }))
    );

    if (Math.round(trees) !== EXPECTED_TREES_TOTAL) {
      const missing = EXPECTED_TREES_TOTAL - Math.round(trees);
      console.warn(`[Analytics] Off by ${missing}. Every numeric column on each pool:`);
      const cs = columnSums(completedPool);
      console.table(
        Object.entries(cs)
          .map(([id, sum]) => ({
            board: "Completed Portfolio",
            id,
            title: titleFor(completedCols, id),
            sum,
          }))
          .sort((a, b) => b.sum - a.sum)
      );
      const ps = columnSums(inauguratedPool);
      console.table(
        Object.entries(ps)
          .map(([id, sum]) => ({
            board: "Portfolio New",
            id,
            title: titleFor(portfolioCols, id),
            sum,
          }))
          .sort((a, b) => b.sum - a.sum)
      );
    }
  }

  const discoveryMap = {
    headcountColId,
    periodColId,
    maintainedColId,
    stageColId,
    exclusionColId,
    completedSiteTypeId,
    statusColId,
    treeColsPortfolio,
    treeColsCompleted,
  };

  const stats = [
    { label: LABELS.employees, value: Math.round(employees).toLocaleString(), unit: "" },
    { label: LABELS.openSpaces, value: openSpaces.toLocaleString(), unit: "" },
    { label: LABELS.activeProjects, value: activeItems.length.toLocaleString(), unit: "" },
    { label: LABELS.trees, value: Math.round(trees).toLocaleString(), unit: "" },
  ];

  return { discoveryMap, stats };
};

/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------ */
const AnalyticsSection = () => {
  const cachedStats = useRef(loadStatsCache()).current;
  const [data, setData] = useState(cachedStats || []);
  const [loading, setLoading] = useState(!cachedStats);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!MONDAY_TOKEN) {
      setError("Missing API credentials.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const discoveryMap = loadDiscoveryCache();

        let stats;
        if (discoveryMap) {
          stats = await runFastFetch(discoveryMap);
        } else {
          const result = await runDiscovery();
          saveDiscoveryCache(result.discoveryMap);
          stats = result.stats;
        }

        if (cancelled) return;
        setData(stats);
        setLoading(false);
        saveStatsCache(stats);
      } catch (err) {
        console.error("Analytics fetch error:", err.message);
        if (cancelled) return;
        // If we had cached stats showing already, leave them up rather than
        // replacing a good view with an error banner.
        if (!cachedStats) setError(err.message);
        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <section className="analytics-section">
        <p className="analytics-status">Loading analytics…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="analytics-section">
        <p className="analytics-status">Failed to load analytics.</p>
      </section>
    );
  }

  return (
    <section className="analytics-section">
      <div className="analytics-stats">
        {data.map((item, index) => (
          <div className="analytics-stat" key={index}>
            <p className="analytics-label">{item.label}</p>

            <div className="analytics-value-row">
              <h2 className="analytics-value">{item.value}</h2>
              {item.unit ? (
                <span className="analytics-unit">{item.unit}</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AnalyticsSection;